import {
  useQueryStates,
  parseAsString,
  parseAsArrayOf,
  parseAsIsoDate,
  createParser,
} from 'nuqs';
import { useMemo, useCallback } from 'react';
import type { CodingSearchParams } from '../components/CodingSearchForm';
import type { CodingTags } from '../types/codingData.types';

// Custom parser for CodingTags (serializes as "archived:false,EMR_filled:true")
const parseAsCodingTags = createParser({
  parse: (value: string): CodingTags | null => {
    if (!value) return null;
    const tags: CodingTags = {};
    for (const part of value.split(',')) {
      const [key, val] = part.split(':');
      if (key === 'archived') tags.archived = val === 'true';
      if (key === 'EMR_filled') tags.EMR_filled = val === 'true';
    }
    return Object.keys(tags).length > 0 ? tags : null;
  },
  serialize: (value: CodingTags): string => {
    const parts: string[] = [];
    if (value.archived !== undefined) parts.push(`archived:${value.archived}`);
    if (value.EMR_filled !== undefined)
      parts.push(`EMR_filled:${value.EMR_filled}`);
    return parts.join(',');
  },
  eq: (a: CodingTags, b: CodingTags): boolean =>
    a.archived === b.archived && a.EMR_filled === b.EMR_filled,
});

// Helper to get date 6 weeks ago
const getSixWeeksAgo = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() - 42);
  return date;
};

// URL param parsers configuration
const searchParamsParsers = {
  phrase: parseAsString.withDefault(''),
  start_date: parseAsIsoDate,
  end_date: parseAsIsoDate,
  coding_status: parseAsArrayOf(parseAsString).withDefault([]),
  visit_types: parseAsArrayOf(parseAsString).withDefault([]),
  clinics: parseAsArrayOf(parseAsString).withDefault([]),
  tags: parseAsCodingTags,
};

/**
 * Hook to sync CodingSearchParams with URL query parameters using nuqs.
 * nuqs is the single source of truth - no separate useState needed.
 */
export function useCodingSearchParams() {
  const [urlParams, setUrlParams] = useQueryStates(searchParamsParsers, {
    history: 'replace',
  });

  // Check if URL has any params set
  const hasUrlParams =
    urlParams.start_date !== null ||
    urlParams.end_date !== null ||
    urlParams.phrase !== '' ||
    urlParams.coding_status.length > 0 ||
    urlParams.visit_types.length > 0 ||
    urlParams.clinics.length > 0 ||
    urlParams.tags !== null;

  // Convert nuqs params to CodingSearchParams format with defaults
  const searchParams = useMemo(
    (): CodingSearchParams => ({
      phrase: urlParams.phrase,
      start_date:
        urlParams.start_date ?? (hasUrlParams ? null : getSixWeeksAgo()),
      end_date: urlParams.end_date,
      coding_status: urlParams.coding_status,
      visit_types: urlParams.visit_types,
      clinics: urlParams.clinics,
      tags: urlParams.tags ?? (hasUrlParams ? undefined : { archived: false }),
    }),
    [urlParams, hasUrlParams]
  );

  // Update URL params from CodingSearchParams
  const setSearchParams = useCallback(
    (params: CodingSearchParams) => {
      setUrlParams({
        phrase: params.phrase || null,
        start_date: params.start_date,
        end_date: params.end_date,
        coding_status:
          params.coding_status.length > 0 ? params.coding_status : null,
        visit_types: params.visit_types.length > 0 ? params.visit_types : null,
        clinics: params.clinics.length > 0 ? params.clinics : null,
        tags:
          params.tags && Object.keys(params.tags).length > 0
            ? params.tags
            : null,
      });
    },
    [setUrlParams]
  );

  // Clear all URL params
  const clearSearchParams = useCallback(() => {
    setUrlParams({
      phrase: null,
      start_date: null,
      end_date: null,
      coding_status: null,
      visit_types: null,
      clinics: null,
      tags: null,
    });
  }, [setUrlParams]);

  return {
    /** Current search params (derived from URL) */
    searchParams,
    /** Update search params (syncs to URL) */
    setSearchParams,
    /** Clear all search params from URL */
    clearSearchParams,
  };
}
